<template>
  <div class="mob-menu">
    <div class="mo-menu-area">
      <header class="flex-btw">
        <img
          src="/images/logo-sconchat-full.png"
          alt="Logo"
          class="logo"
          aria-label="Go home"
          @click="navigateTo('ss-dashboard', 'label_home')"
        />
        <div class="rt">
          <!--크레딧및플랜-->
          <ButtonCreditInfo v-if="isUsingCredit" :using-auto-update="isUsingCredit" />
          <button type="button" class="menu-close-btn" @click="emit('close')">
            <IconClose />
          </button>
        </div>
      </header>

      <div class="gnb-menu">
        <div>
          <div class="globel-wrap">
            <!--언어-->
            <button class="m-btn global-btn" @click="toggleLanguageModal">
              <img src="/public/icons/global.svg" alt="" />
              <span class="accent">{{ t('language_meta') }}</span>
            </button>
          </div>
          <div class="nav">
            <ul class="nav-list">
              <!-- 통역 -->
              <li>
                <div class="menu-item" :class="{ open: isOpen(0) }" @click="toggle(0)">
                  <span>{{ t('label_interpretation') }}</span>
                  <IconArrow />
                </div>
                <ul :ref="(el) => (inrBoxRefs[0] = el as HTMLElement)" class="inr-box" :style="getBodyStyle(0)">
                  <li>
                    <button
                      type="button"
                      class="mo-menu-btn"
                      @click="navigateTo('ss-chat-conference', 'label_conference')"
                    >
                      {{ t('label_conference') }}
                    </button>
                  </li>
                  <li>
                    <button type="button" class="mo-menu-btn" @click="navigateTo('ss-chat-meeting', 'label_meeting')">
                      {{ t('label_meeting') }}
                    </button>
                  </li>
                  <li>
                    <button type="button" class="mo-menu-btn" @click="navigateTo('ss-chat-audience', 'label_audience')">
                      {{ t('label_audience') }}
                    </button>
                  </li>
                  <li>
                    <button type="button" class="mo-menu-btn" @click="navigateTo('ss-chat-general', 'label_general')">
                      {{ t('label_general') }}
                    </button>
                  </li>
                  <li>
                    <button type="button" class="mo-menu-btn" @click="navigateTo('ss-chat-single', 'label_single')">
                      {{ t('label_single') }}
                    </button>
                  </li>
                  <li>
                    <button type="button" class="mo-menu-btn" @click="navigateTo('ss-chat-list', 'my_interpreter')">
                      {{ t('my_interpreter') }}
                    </button>
                  </li>
                </ul>
              </li>
              <!-- 관리 -->
              <li>
                <div class="menu-item" :class="{ open: isOpen(1) }" @click="toggle(1)">
                  <span>{{ t('label_settings') }}</span>
                  <IconArrow></IconArrow>
                </div>
                <ul :ref="(el) => (inrBoxRefs[1] = el as HTMLElement)" class="inr-box" :style="getBodyStyle(1)">
                  <li>
                    <button
                      type="button"
                      class="mo-menu-btn"
                      @click="navigateTo('ss-chat-history', 'interpretation_history')"
                    >
                      {{ t('interpretation_history') }}
                    </button>
                  </li>
                  <li v-if="userType === 'HOST'">
                    <button type="button" class="mo-menu-btn" @click="navigateTo('ss-plan-manage', 'plan_setting')">
                      {{ t('plan_setting') }}
                    </button>
                  </li>
                </ul>
              </li>

              <!-- 설정 -->
              <li>
                <div class="menu-item" :class="{ open: isOpen(2) }" @click="toggle(2)">
                  <span>{{ t('setting') }}</span>
                  <IconArrow></IconArrow>
                </div>
                <ul :ref="(el) => (inrBoxRefs[2] = el as HTMLElement)" class="inr-box" :style="getBodyStyle(2)">
                  <li>
                    <button type="button" class="mo-menu-btn" @click="navigateTo('ss-contact', 'support_center')">
                      {{ t('support_center') }}
                    </button>
                  </li>
                  <li>
                    <button type="button" class="mo-menu-btn" @click="toggleSettingModal('PROFILE')">
                      {{ t('label_edit_my_info') }}
                    </button>
                  </li>

                  <li
                    v-if="
                      ['STARTER', 'PRO', 'ENTERPRISE'].includes(subscriptionStore.currentPlan) && userType === 'HOST'
                    "
                  >
                    <button type="button" class="mo-menu-btn" @click="toggleSettingModal('EXTRA_USERS')">
                      {{ t('label_extra_users') }}
                    </button>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>

        <button type="button" class="logout-btn" @click="logout">
          <IconExit />
          <span>{{ t('logout') }}</span>
        </button>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <ModalSelectLanguage
      v-if="showLanguageModal"
      :current-language="getLanguageByCode(userStore.pageLanguage)"
      :subtitle-key="t('label_choose_display_language')"
      @close="toggleLanguageModal"
    />
    <SelfServiceSettingModal v-if="settingMenu" :menu="settingMenu" @close="closeAll" />
  </Teleport>
</template>

<script setup lang="ts">
import { ref, nextTick, computed, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

import ButtonCreditInfo from '@/components/domains/chat/common/ButtonCreditInfo.vue';
import ModalSelectLanguage from '@/components/domains/chat/common/ModalSelectLanguage.vue';
import IconArrow from '@/components/icons/IconArrow.vue';
import IconClose from '@/components/icons/IconClose.vue';
import IconExit from '@/components/icons/IconExit.vue';
import SelfServiceSettingModal from '@/components/self-service/modal/SelfServiceSettingModal.vue';
import { useGtag } from '@/composables/shared/useGtag';
import { getLanguageByCode } from '@/static/languages';
import { useAdminStore } from '@/stores/admin';
import { useSubscriptionStore } from '@/stores/subscription';
import { useUserStore } from '@/stores/user';
import { TypeUser } from '@/types/common';
import { SelfServiceSettingMenu } from '@/types/components';

withDefaults(
  defineProps<{
    domain?: 'mobile' | 'self-service';
    isUsingCredit?: boolean;
  }>(),
  {
    domain: 'mobile',
    isUsingCredit: false,
  }
);

const { t } = useI18n();
const router = useRouter();

const userStore = useUserStore();
const adminStore = useAdminStore();
const subscriptionStore = useSubscriptionStore();

const { trackEventClick } = useGtag();

const showLanguageModal = ref<boolean>(false);

const emit = defineEmits(['close']);

const userType = computed<TypeUser>(() => {
  const { userType = '' } = userStore.userInfo || {};
  return userType;
});

// 각 메뉴별 아코디언 상태
const openIndex = ref<number | null>(null);
const inrBoxRefs = ref<(HTMLElement | null)[]>([]);
const heights = ref(['0px', '0px', '0px']);
const displays = ref(['none', 'none', 'none']);
const settingMenu = ref<SelfServiceSettingMenu | null>(null);

const navigateTo = (name: string, label: string, query: Record<string, string> = {}) => {
  if (name) {
    // trackEventClick('mobile_side_menu', t(label, {}, { locale: 'ko' }));

    router.push({ name, query });
    emit('close');
  }
};

const toggleLanguageModal = () => {
  showLanguageModal.value = !showLanguageModal.value;

  if (!showLanguageModal.value) {
    emit('close');
  }
};

const handleSettingBack = () => {
  history.pushState(history.state, '', location.href);
  toggleSettingModal();
};

const closeAll = () => {
  toggleSettingModal();
  emit('close');
};

const toggleSettingModal = (menu: SelfServiceSettingMenu | null = null) => {
  if (menu) {
    history.pushState(history.state, '', location.href);
    window.addEventListener('popstate', handleSettingBack);
  } else {
    window.removeEventListener('popstate', handleSettingBack);
  }
  settingMenu.value = menu;
};

onUnmounted(() => {
  window.removeEventListener('popstate', handleSettingBack);
});

const toggle = async (index: number) => {
  const el = inrBoxRefs.value[index];
  if (!el) return;

  const isCurrentlyOpen = openIndex.value === index;

  // 열려있는 메뉴 닫기
  if (openIndex.value !== null) {
    const prevIndex = openIndex.value;
    const prevEl = inrBoxRefs.value[prevIndex];
    heights.value[prevIndex] = `${inrBoxRefs.value[prevIndex]?.scrollHeight}px`;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        heights.value[prevIndex] = '0px';
      });
    });
    // ✅ transitionend 이벤트로 정확한 타이밍에 display: none 처리
    const onTransitionEnd = () => {
      if (openIndex.value !== prevIndex) {
        displays.value[prevIndex] = 'none';
      }
      prevEl?.removeEventListener('transitionend', onTransitionEnd);
    };
    prevEl?.addEventListener('transitionend', onTransitionEnd);
  }

  if (isCurrentlyOpen) {
    // 같은 메뉴 클릭 시 닫기
    openIndex.value = null;
    const onTransitionEnd = () => {
      displays.value[index] = 'none';
      el.removeEventListener('transitionend', onTransitionEnd);
    };
    el.addEventListener('transitionend', onTransitionEnd);
  } else {
    // 새 메뉴 열기
    openIndex.value = index;
    displays.value[index] = 'block';

    await nextTick();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        heights.value[index] = `${el.scrollHeight}px`;
      });
    });
  }
};

const getBodyStyle = (index: number) => ({
  height: heights.value[index],
  display: displays.value[index],
  overflow: 'hidden',
  transition: 'height 0.3s ease',
});

const isOpen = (index: number) => openIndex.value === index;

const logout = async () => {
  await userStore.requestLogout();
  const isHost = adminStore.isHost;
  navigateTo('login-ss', 'button_logout', {
    partnerCode: isHost ? '' : adminStore.getPartnerInfo.partnerCode,
  });
};
</script>

<style scoped lang="scss">
.mob-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 100dvw;
  height: 100dvh;
  z-index: 100;
  will-change: transform;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  overflow-x: none;
  .mo-menu-area {
    background: #fff;
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    header {
      height: 64px;
      border-bottom: 1px solid $color-gray-40;
      padding: 0 1.25rem;
      .rt {
        .menu-close-btn {
          padding: 2px;
          border-radius: $size-s;
          svg {
            width: 1.5rem;
            height: 1.5rem;
          }
        }
      }
      .logo {
        display: block;
        position: relative;
        height: 30px;
      }
    }
  }

  .gnb-menu {
    flex: 1;
    overflow-y: auto;
    padding: 1.25rem;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    .globel-wrap {
      .global-btn {
        justify-content: flex-start;
        padding: 0.75rem 0;
        img {
          width: 18px;
          height: 18px;
          object-fit: contain;
        }
        span {
          font-size: $font-size-p;
        }
      }
    }
    .nav {
      height: 100%;
      .nav-list {
        > li {
          margin-top: 2rem;
          .menu-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: $size-m;
            cursor: pointer;
            span {
              font-size: $font-size-h4;
              font-weight: 600;
            }
            svg {
              width: 1.5rem;
              height: 1.5rem;
              transform: rotate(180deg);
              transition: all 0.3s;
            }
            &.open {
              svg {
                transform: rotate(0);
              }
            }
          }
          .inr-box {
            border-radius: $size-m;
            padding: $size-m;
            background: $gray10;
            li {
              .mo-menu-btn {
                font-size: $font-size-p;
                padding: 0.5rem 0.75rem;
                font-weight: 400;
                width: 100%;
                display: block;
                text-align: left;
              }
            }
          }
        }
      }
    }
    .logout-btn {
      padding: 0.5rem 0;
      margin-bottom: 20px;
      span {
        font-size: $font-size-p;
      }
    }
  }
}
</style>
